import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { BiSidebar } from 'react-icons/bi';
import { FiLink } from 'react-icons/fi';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  User,
  Calendar,
  Tag,
  FileText,
  AlertTriangle,
  DollarSign,
  ArrowRight,
  Link as LinkIcon,
  AtSign,
  ThumbsUp,
  ThumbsDown,
  GitBranch,
  X as XIcon,
  Braces,
  Brackets,
  Users,
  Check,
  ListChecks,
  RefreshCw,
  FileX,
  AlertCircle,
  Loader2,
  Info,
  CheckSquare,
  AlertOctagon,
  GitCommit,
  ChevronDown,
  Zap,
  Briefcase,
  MessageSquarePlus,
  X,
  Clock,
  AlignLeft,
} from 'lucide-react';

import ChatList from './ChatList';
import Sidebar from './Sidebar';
import { DataState } from '../context/DataProvider';
import AutoResizableTextarea from './AutoResizableTextarea';
import Tabbar from './base/Tabbar';
import Integration from './Integration';
import IntegrationTutorial from './IntegrationTutorial';
import { uploadFiles, uploadImages } from '../services/api';
import RightSideActionTracker from './chat/RightSideActionTracker';
import { useNotifications } from '../context/NotificationProvider';
import { useChangeRequests } from '../context/ChangeRequestProvider';
import CRDetailModal from './modals/CRDetailModal';
import EmailComponent from './chat/EmailModal';
import { useResourceAllocation } from '../context/ResourceAllocationProvider';
import ResourceAllocationModal from './modals/ResourceAllocationModal';
import { ProcessedContent } from './meeting/ProcessedMeetingContent';
import MeetingDetailModal from './meeting/MeetingDetailModal';
import {
  getMeetingDetails,
  getUserConfiguredMeetingDetails,
  submitWorkflowApproval,
} from '../services/workflowApi';
import { appImages } from '../utils/constants';
import FeedbackComponent from './form/FeedbackComponent';
import { sanitizeHtml } from '../utils/sanitize';
import {
  ENABLE_FEEDBACK,
  ENABLE_INTEGRATION_GMAIL,
  ENABLE_MCP,
  ENABLE_MEETINGS,
} from '../config/features';
import { integrationData } from '../utils/constants';

const useOnClickOutside = (ref, handler, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler, enabled]);
};

// ✅ DetailItem Component
const DetailItem = ({ icon: Icon, label, children, className }) => {
  const type = typeof children;
  const isReactElement = React.isValidElement(children);
  const isPlainObject =
    typeof children === 'object' &&
    children !== null &&
    !Array.isArray(children) &&
    !isReactElement &&
    children.constructor === Object;
  const isArray = Array.isArray(children);

  const renderPrimitiveValue = () => {
    if (type === 'boolean')
      return (
        <span
          className={`ml-2 font-mono font-semibold ${children ? 'text-emerald-400' : 'text-red-400'}`}
        >
          {children.toString()}
        </span>
      );
    if (type === 'number')
      return (
        <span className="ml-2 font-mono text-sky-300">
          {children.toLocaleString()}
        </span>
      );
    if (type === 'string' || children === null || children === undefined) {
      return <span className="ml-2 text-zinc-300">{String(children)}</span>;
    }
    return null;
  };

  const renderJsonValue = (value, isArray) => {
    let jsonString;
    try {
      jsonString = JSON.stringify(value, null, 2);
    } catch (e) {
      jsonString = '[Error]';
    }
    let isTruncated = false;
    if (jsonString.length > 1000) {
      jsonString = jsonString.substring(0, 1000);
      isTruncated = true;
    }
    return (
      <div className="mt-2 w-full">
        <div className="mb-1 flex items-center font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          {isArray ? (
            <Brackets className="mr-1.5 h-3 w-3" />
          ) : (
            <Braces className="mr-1.5 h-3 w-3" />
          )}
          {isArray ? 'Array' : 'Object'}
        </div>
        <pre className="custom-scrollbar w-full overflow-x-auto whitespace-pre-wrap rounded-lg border border-zinc-800/50 bg-zinc-950/50 px-3 py-2.5 font-mono text-[11px] text-amber-300 shadow-inner">
          {jsonString}
          {isTruncated && '\n...'}
        </pre>
      </div>
    );
  };

  return (
    <div className={`group flex items-start text-xs ${className}`}>
      <div className="mr-3 mt-0.5 shrink-0 rounded-md bg-zinc-800/50 p-1 text-zinc-400 transition-colors group-hover:bg-zinc-800 group-hover:text-zinc-300">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <span className="mr-1 font-medium text-zinc-500">{label}:</span>
        {isPlainObject && renderJsonValue(children, false)}
        {isArray && renderJsonValue(children, true)}
        {isReactElement && <span className="ml-2">{children}</span>}
        {!isPlainObject &&
          !isArray &&
          !isReactElement &&
          renderPrimitiveValue()}
      </div>
    </div>
  );
};

const JiraNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="custom-scrollbar h-full overflow-y-auto p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DetailItem icon={User} label="Assignee">
          {data.assignee}
        </DetailItem>
        <DetailItem icon={Calendar} label="Due Date">
          {data.dueDate}
        </DetailItem>
        <DetailItem icon={AlertTriangle} label="Priority">
          {data.priority}
        </DetailItem>
        <DetailItem icon={Tag} label="Status">
          {data.status}
        </DetailItem>
      </div>
      <div className="mt-4 border-t border-zinc-800 pt-4">
        <p className="mb-2 text-xs font-semibold text-zinc-400">Description</p>
        <p className="rounded-lg border border-zinc-700/50 bg-zinc-800/30 p-4 text-sm text-zinc-300">
          {data.description}
        </p>
      </div>
      <a
        href={data.link}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center rounded-lg border border-[#5652CF]/20 bg-[#5652CF]/10 px-3 py-2 text-xs font-medium text-blue-400 transition-colors hover:border-[#5652CF]/40 hover:text-blue-300"
      >
        View in Jira <ArrowRight className="ml-2 h-3 w-3" />
      </a>
    </div>
  );
};

const GmailNotificationDetail = ENABLE_INTEGRATION_GMAIL
  ? ({ notification }) => {
      const { payload: data } = notification.listener_data;
      return (
        <div className="custom-scrollbar h-full overflow-y-auto p-6">
          <div className="mb-4 space-y-3 border-b border-zinc-800 pb-4 text-xs">
            <div className="flex items-center gap-2 rounded-lg bg-zinc-800/30 p-2">
              <span className="w-10 font-semibold text-zinc-400">From:</span>{' '}
              <span className="text-zinc-200">{data.from}</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-zinc-800/30 p-2">
              <span className="w-10 font-semibold text-zinc-400">To:</span>{' '}
              <span className="text-zinc-200">{data.to}</span>
            </div>
          </div>
          <div
            className="prose prose-sm prose-invert max-w-none rounded-xl border border-white/5 bg-white/5 p-4 text-zinc-300"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.body) }}
          />
        </div>
      );
    }
  : null;

const ZohoCrmNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="custom-scrollbar h-full overflow-y-auto p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DetailItem icon={Briefcase} label="Company">
          {data.leadName}
        </DetailItem>
        <DetailItem icon={User} label="Contact">
          {data.contactPerson}
        </DetailItem>
        <DetailItem icon={AtSign} label="Email">
          {data.contactEmail}
        </DetailItem>
        <DetailItem icon={DollarSign} label="Value">
          ${data.dealSize.toLocaleString()}
        </DetailItem>
        <DetailItem icon={LinkIcon} label="Source">
          {data.source}
        </DetailItem>
      </div>
      <div className="mt-4 border-t border-zinc-800 pt-4">
        <p className="mb-2 text-xs font-semibold text-zinc-400">Notes</p>
        <p className="rounded-lg border border-zinc-700/50 bg-zinc-800/30 p-4 text-sm text-zinc-300">
          {data.notes}
        </p>
      </div>
      <a
        href={data.link}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center rounded-lg border border-[#5652CF]/20 bg-[#5652CF]/10 px-3 py-2 text-xs font-medium text-blue-400 transition-colors hover:border-[#5652CF]/40 hover:text-blue-300"
      >
        View in Zoho CRM <ArrowRight className="ml-2 h-3 w-3" />
      </a>
    </div>
  );
};

const SlackNotificationDetail = ({ notification }) => {
  const { payload: data } = notification.listener_data;
  return (
    <div className="custom-scrollbar h-full overflow-y-auto p-6">
      <div className="rounded-xl border border-zinc-700/50 bg-zinc-800/40 p-5">
        <div className="mb-3 flex items-center">
          <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-700">
            <User className="h-4 w-4 text-zinc-300" />
          </div>
          <span className="text-sm font-bold text-zinc-200">{data.user}</span>
        </div>
        <p className="text-sm leading-relaxed text-zinc-300">{data.message}</p>
      </div>
      <a
        href={data.link}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center rounded-lg border border-[#5652CF]/20 bg-[#5652CF]/10 px-3 py-2 text-xs font-medium text-blue-400 transition-colors hover:border-[#5652CF]/40 hover:text-blue-300"
      >
        View in Slack <ArrowRight className="ml-2 h-3 w-3" />
      </a>
    </div>
  );
};

const ToolApprovalCard = ({
  toolId,
  toolData,
  onDecision,
  onReasonChange,
  decision = {},
}) => {
  const targetAppIcon = appImages[toolData.app];
  let parsedArguments = {};
  if (toolData.arguments) {
    if (typeof toolData.arguments === 'object' && toolData.arguments !== null) {
      parsedArguments = toolData.arguments;
    } else if (
      typeof toolData.arguments === 'string' &&
      toolData.arguments.trim()
    ) {
      try {
        parsedArguments = JSON.parse(toolData.arguments);
      } catch (error) {
        toast.error(`Failed to parse arguments for ${toolData.name}`);
      }
    }
  }
  return (
    <div
      className={`rounded-xl border p-4 shadow-sm transition-all duration-300 ${decision.approved === true ? 'border-emerald-800/50 bg-emerald-950/20 shadow-emerald-900/10' : decision.approved === false ? 'border-red-800/50 bg-red-950/20 shadow-red-900/10' : 'border-zinc-700/50 bg-zinc-800/30'}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="mb-3 flex items-center text-sm">
            <div className="mr-2 rounded-md bg-white p-1">
              <img src={targetAppIcon} alt={toolData.app} className="h-3 w-3" />
            </div>
            <span className="font-bold capitalize text-zinc-100">
              {toolData.name}
            </span>
          </div>
          <div className="space-y-2 border-l-2 border-zinc-700 pl-1">
            {Object.keys(parsedArguments).length > 0 ? (
              Object.entries(parsedArguments).map(([key, value]) => (
                <DetailItem
                  icon={GitBranch}
                  label={key.replace(/_/g, ' ')}
                  key={key}
                >
                  {value}
                </DetailItem>
              ))
            ) : (
              <p className="pl-2 text-xs italic text-zinc-500">
                No arguments provided.
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3 border-t border-zinc-700/30 pt-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onDecision(toolId, true, toolData.arguments)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${decision.approved === true ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20' : 'bg-zinc-700 text-zinc-200 hover:bg-emerald-600/80'}`}
          >
            <ThumbsUp className="h-3.5 w-3.5" /> Approve
          </button>
          <button
            onClick={() => onDecision(toolId, false, toolData.arguments)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${decision.approved === false ? 'bg-red-600 text-white shadow-lg shadow-red-900/20' : 'bg-zinc-700 text-zinc-200 hover:bg-red-600/80'}`}
          >
            <ThumbsDown className="h-3.5 w-3.5" /> Deny
          </button>
        </div>
        {decision.approved === false && (
          <input
            type="text"
            placeholder="Reason for denial (required)"
            value={decision.disapprovalReason || ''}
            onChange={(e) => onReasonChange(toolId, e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900/50 px-3 py-2 text-xs text-zinc-200 placeholder-zinc-500 transition focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50"
          />
        )}
      </div>
    </div>
  );
};

const ApprovalNotificationDetail = ({
  notification,
  onClose,
  onReadyStateChange,
}) => {
  const { workflow_data: data } = notification;
  const { userGoogleId: accessToken } = DataState();
  const { removeNotification } = useNotifications();
  const [toolDecisions, setToolDecisions] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleToolDecision = (toolId, approved, toolArguments) => {
    setToolDecisions((prev) => ({
      ...prev,
      [toolId]: {
        arguments: toolArguments,
        approved,
        disapproved: !approved,
        disapprovalReason: approved
          ? ''
          : prev[toolId]?.disapprovalReason || '',
      },
    }));
  };
  const handleReasonChange = (toolId, value) => {
    setToolDecisions((prev) => ({
      ...prev,
      [toolId]: { ...prev[toolId], disapprovalReason: value },
    }));
  };
  const isApprovalReady = useMemo(() => {
    if (data.node_type !== 'action') return false;
    const tools = data.approvalNeededTools;
    return Object.keys(tools).every((toolId) => {
      const decision = toolDecisions[toolId];
      if (!decision) return false;
      if (decision.approved) return true;
      return decision.disapproved && decision.disapprovalReason?.trim() !== '';
    });
  }, [data, toolDecisions]);

  const handleSubmit = useCallback(async () => {
    if (!isApprovalReady) {
      toast.error(
        'Please make a decision for all tools and provide reasons for denials.',
      );
      return;
    }
    setIsLoading(true);
    const { execution_id, node_id } = data;
    try {
      await submitWorkflowApproval(
        accessToken,
        execution_id,
        node_id,
        toolDecisions,
      );
      toast.success('Approval submitted successfully!');
      removeNotification(notification.id);
      onClose();
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : error?.detail || error?.message || 'Error',
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    accessToken,
    data,
    isApprovalReady,
    notification.id,
    onClose,
    removeNotification,
    toolDecisions,
  ]);

  useEffect(() => {
    onReadyStateChange({
      isReady: isApprovalReady,
      isLoading: isLoading,
      submit: handleSubmit,
    });
  }, [isApprovalReady, isLoading, handleSubmit, onReadyStateChange]);

  const renderActionApproval = () => (
    <div>
      <div className="mb-6 rounded-xl border border-zinc-700/50 bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 p-4 shadow-sm">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
          <Zap className="h-4 w-4 text-amber-400" />{' '}
          {data.node_description || data.node_title}
        </h4>
      </div>
      <div className="space-y-4">
        {Object.entries(data.approvalNeededTools).map(([toolId, toolData]) => (
          <ToolApprovalCard
            key={toolId}
            toolId={toolId}
            toolData={toolData}
            onDecision={handleToolDecision}
            onReasonChange={handleReasonChange}
            decision={toolDecisions[toolId]}
          />
        ))}
      </div>
    </div>
  );

  const renderHumanApproval = () => (
    <div className="space-y-4 rounded-xl border border-zinc-700/50 bg-zinc-800/30 p-4">
      {Object.entries(data.details).map(([key, value]) => (
        <div key={key} className="flex items-start text-sm">
          <span className="w-1/3 font-semibold capitalize text-zinc-400">
            {key.replace(/_/g, ' ')}:
          </span>
          <span className="w-2/3 rounded border border-zinc-800 bg-zinc-900/50 px-2 py-1 text-zinc-200">
            {value}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="custom-scrollbar h-full overflow-y-auto p-6">
      <p className="mb-5 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
        <GitCommit className="h-3 w-3" /> Workflow:{' '}
        <span className="text-zinc-300">{data.workflow_title}</span>
      </p>
      {data.node_type === 'action' && renderActionApproval()}
      {data.node_type === 'human' && renderHumanApproval()}
    </div>
  );
};

const parseUTCDateString = (dateInput) => {
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput !== 'string' || !dateInput) return new Date(null);
  const hasOffset =
    dateInput.endsWith('Z') ||
    dateInput.slice(-6).includes('+') ||
    dateInput.slice(-6).includes('-');
  const correctedString = hasOffset ? dateInput : dateInput + 'Z';
  return new Date(correctedString);
};

// ✅ SUPER COMPACT: Footer Control Panel
const MeetingSubmissionControls = ({
  notification,
  processedData,
  transcriptContent,
  onClose,
  onStageChat,
}) => {
  const { isBusy } = DataState();
  const [selections, setSelections] = useState({
    includeTranscript: true,
    overall_summary: true,
    member_wise_updates: true,
    task_wise_summary: true,
    unique_blockers: true,
    all_follow_ups: true,
    extracted_action_items: true,
    next_steps: true,
  });

  const handleSelectionChange = (e) => {
    const { name, checked } = e.target;
    setSelections((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = () => {
    if (isBusy) return;
    const payloadToSend = {
      event_id: notification.id,
      meeting_id: notification.meeting_id,
      source_type: notification.source_type,
      app: 'microsoft_teams',
      event_name: notification.event_name,
      timestamp: notification.timestamp,
      subject: notification.subject,
      organizer: notification.organizer,
      attendees: notification.attendees,
      start: notification.start,
      end: notification.end,
      description: notification.bodyPreview,
      transcript_status: notification.transcript_status,
    };
    if (selections.includeTranscript && transcriptContent)
      payloadToSend.transcriptContent = transcriptContent;
    if (processedData) {
      payloadToSend.llm_summary = {};
      Object.keys(selections).forEach((key) => {
        if (
          key !== 'includeTranscript' &&
          selections[key] &&
          processedData[key]
        )
          payloadToSend.llm_summary[key] = processedData[key];
      });
    }
    onStageChat(payloadToSend, notification.id);
  };

  // ✅ Optimized Option Tile (Lower padding, smaller text)
  const OptionTile = ({ name, label, icon: Icon, checked, onChange }) => (
    //eslint-disable-next-line
    <div
      onClick={() => onChange({ target: { name, checked: !checked } })}
      className={`flex cursor-pointer select-none items-center gap-2 rounded-md border px-2.5 py-1.5 text-[10px] transition-all duration-200 ${
        checked
          ? 'border-[#5652CF]/40 bg-[#5652CF]/10 text-blue-100 shadow-[0_0_15px_-3px_rgba(59,130,246,0.15)]'
          : 'border-zinc-700/50 bg-zinc-800/40 text-zinc-400 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-300'
      }`}
    >
      <div
        className={`flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border transition-colors ${checked ? 'border-[#5652CF] bg-[#5652CF]' : 'border-zinc-600 bg-transparent group-hover:border-zinc-500'}`}
      >
        {checked && <Check className="h-3 w-3 stroke-[3px] text-white" />}
      </div>
      <div className="flex min-w-0 items-center gap-1.5">
        {Icon && (
          <Icon
            className={`h-3 w-3 ${checked ? 'text-blue-400' : 'text-zinc-500 group-hover:text-zinc-400'}`}
          />
        )}
        <span className="truncate font-medium">{label}</span>
      </div>
    </div>
  );

  return (
    <div className="relative z-20 flex flex-col items-center justify-between gap-4 border-t border-zinc-800/50 bg-[#121214] px-4 py-3 shadow-[0_-5px_20px_rgba(0,0,0,0.4)] md:flex-row">
      {(processedData || transcriptContent) && (
        <div className="flex flex-1 flex-wrap justify-center gap-2 md:justify-start">
          {transcriptContent && (
            <OptionTile
              icon={FileText}
              name="includeTranscript"
              label="Full Transcript"
              checked={selections.includeTranscript}
              onChange={handleSelectionChange}
            />
          )}
          {processedData?.overall_summary && (
            <OptionTile
              icon={AlignLeft}
              name="overall_summary"
              label="Summary"
              checked={selections.overall_summary}
              onChange={handleSelectionChange}
            />
          )}
          {processedData?.member_wise_updates && (
            <OptionTile
              icon={Users}
              name="member_wise_updates"
              label="Member Updates"
              checked={selections.member_wise_updates}
              onChange={handleSelectionChange}
            />
          )}
          {processedData?.task_wise_summary && (
            <OptionTile
              icon={CheckSquare}
              name="task_wise_summary"
              label="Task Updates"
              checked={selections.task_wise_summary}
              onChange={handleSelectionChange}
            />
          )}
          {processedData?.unique_blockers && (
            <OptionTile
              icon={AlertOctagon}
              name="unique_blockers"
              label="Blockers"
              checked={selections.unique_blockers}
              onChange={handleSelectionChange}
            />
          )}
          {processedData?.extracted_action_items && (
            <OptionTile
              icon={ListChecks}
              name="extracted_action_items"
              label="Action Items"
              checked={selections.extracted_action_items}
              onChange={handleSelectionChange}
            />
          )}
        </div>
      )}
      <button
        onClick={handleSubmit}
        disabled={isBusy}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-5 py-2.5 text-xs font-semibold text-white transition-all hover:from-[#5652CF]  hover:to-blue-400 hover:shadow-[#5652CF]/25 active:scale-95 disabled:cursor-not-allowed disabled:bg-zinc-600 disabled:from-zinc-600 disabled:to-zinc-600 md:w-auto"
      >
        <MessageSquarePlus className="h-4 w-4" /> Chat with Agent
      </button>
    </div>
  );
};

// ✅ BEAUTIFIED & COMPACTED: Teams Meeting Detail
const TeamsMeetingNotificationDetail = ({
  notification,
  scrollContainerRef,
  onClose,
  onStageChat,
}) => {
  const [status, setStatus] = useState('idle');
  const [transcriptStatus, setTranscriptStatus] = useState(
    notification?.transcript_status || 'idle',
  );
  const [processedData, setProcessedData] = useState(
    notification?.llm_summary || null,
  );
  const [transcriptContent, setTranscriptContent] = useState(
    notification?.transcriptContent || null,
  );
  const { userGoogleId: accessToken } = DataState();
  const [isAtBottom, setIsAtBottom] = useState(true);
  const footerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!notification) return;
    setStatus('loading');
    try {
      let fullUpdatedNotification;
      if (notification.user_provided_meeting) {
        fullUpdatedNotification = await getUserConfiguredMeetingDetails(
          accessToken,
          notification.id,
        );
      } else {
        fullUpdatedNotification = await getMeetingDetails(
          accessToken,
          notification.id,
        );
      }
      setProcessedData(fullUpdatedNotification.llm_summary || null);
      setTranscriptContent(fullUpdatedNotification.transcriptContent || null);
      setTranscriptStatus(fullUpdatedNotification.transcript_status || 'idle');
      setStatus('idle');
    } catch (err) {
      setStatus('failed');
    }
  }, [notification, accessToken]);

  useEffect(() => {
    if (!notification) return;
    setTranscriptStatus(notification.transcript_status || 'idle');
    setTranscriptContent(notification.transcriptContent || null);
    setProcessedData(notification.llm_summary || null);
    const shouldFetch = ['processed', 'reprocessing', 'no_transcript'].includes(
      notification.transcript_status,
    );
    if (!shouldFetch) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  useEffect(() => {
    const scroller = scrollContainerRef.current;
    if (!scroller) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      if (scrollHeight - scrollTop - clientHeight < 20) setIsAtBottom(true);
      else setIsAtBottom(false);
    };
    handleScroll();
    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef, notification]);

  const scrollToFooter = () => {
    footerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const copyHelpers = {
    formatMemberUpdatesForCopy: (updates) => {
      if (!updates) return '';
      return (
        'Member Updates:\n' +
        updates
          .map(
            (upd) =>
              `\nUser: ${upd.speaker_name} (${upd.role})\n- Done: ${upd.completed_tasks_summary}\n- Plan: ${upd.planned_tasks_summary}\n- Blockers: ${upd.blockers_text?.join(', ') || 'None'}`,
          )
          .join('\n')
      );
    },
    formatTaskUpdatesForCopy: (tasks) => {
      if (!tasks) return '';
      return (
        'Task Updates:\n' +
        tasks
          .map(
            (task) =>
              `\nTask: ${task.issue_key}\n- Summary: ${task.consolidated_summary}\n- Status Hint: ${task.status_update_hint || 'N/A'}\n- Dependencies: ${task.dependencies?.map((d) => d.description).join(', ') || 'None'}`,
          )
          .join('\n')
      );
    },
    formatBlockersForCopy: (blockers) => {
      if (!blockers) return '';
      return (
        'Unique Blockers:\n' +
        blockers.map((b) => `- ${b.representative_description}`).join('\n')
      );
    },
  };

  const LoadingState = ({ text }) => (
    <div className="flex flex-col items-center justify-center py-20 text-center text-zinc-500">
      <Loader2 className="mb-3 h-6 w-6 animate-spin text-[#5652CF]" />
      <p className="text-sm font-semibold text-zinc-300">{text}</p>
      <p className="text-[10px]">Fetching latest data...</p>
    </div>
  );
  const NoTranscriptState = () => (
    <div className="flex flex-col items-center justify-center py-20 text-center text-zinc-500">
      <FileX className="mb-3 h-8 w-8 opacity-50" />
      <p className="text-sm font-semibold text-zinc-300">No Transcript</p>
      <p className="text-[10px]">Recording data unavailable.</p>
    </div>
  );
  const FailedState = () => (
    <div className="flex flex-col items-center justify-center py-20 text-center text-red-400">
      <AlertCircle className="mb-3 h-8 w-8" />
      <p className="text-sm font-semibold">Processing Failed</p>
      <p className="text-[10px] text-zinc-500">Could not retrieve data.</p>
    </div>
  );

  const renderContent = () => {
    if (status === 'loading') return <LoadingState text="Updating..." />;
    if (status === 'failed') return <FailedState />;
    switch (transcriptStatus) {
      case 'processing':
      case 'reprocessing':
        return <LoadingState text="Processing Transcript..." />;
      case 'no_transcript':
        return <NoTranscriptState />;
      case 'failed':
        return <FailedState />;
      case 'processed':
        return processedData ? (
          <div className="px-3 pb-4 pt-3 sm:px-5 sm:pb-5">
            <ProcessedContent data={processedData} copyHelpers={copyHelpers} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center text-zinc-500">
            <Info className="mb-3 h-6 w-6 text-blue-400" />
            <p className="text-sm font-semibold text-zinc-400">Data Pending</p>
          </div>
        );
      default:
        return (
          <div className="p-8 text-center text-sm text-zinc-500">
            Status unknown. Refresh to check.
          </div>
        );
    }
  };

  if (!notification) return null;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Top Hero Section - Compacted & Pinned */}
      <div className="relative z-10 shrink-0 border-b border-zinc-800 bg-[#121214] px-4 py-3 shadow-sm sm:px-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="mr-4 min-w-0 flex-1">
            <h2 className="truncate text-base font-bold leading-tight text-zinc-100">
              {notification.subject || 'Untitled Meeting'}
            </h2>
          </div>
          <button
            onClick={fetchData}
            disabled={
              status === 'loading' || transcriptStatus.includes('processing')
            }
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800 px-2.5 py-1.5 text-[10px] font-semibold text-zinc-400 shadow-sm transition-all hover:bg-zinc-700 hover:text-zinc-100"
          >
            <RefreshCw
              className={`h-3 w-3 ${status === 'loading' || transcriptStatus.includes('processing') ? 'animate-spin' : ''}`}
            />
            {status === 'loading' ? 'Refreshing' : 'Refresh'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] text-zinc-400">
          <div className="flex min-w-0 items-center gap-1.5">
            <User className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            <span className="max-w-[150px] truncate">
              {notification.organizer?.emailAddress?.name ||
                notification.organizer?.emailAddress?.address ||
                'Unknown'}
            </span>
          </div>
          <div className="h-3 w-px bg-zinc-700/50"></div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            <span className="font-mono">
              {notification.start?.dateTime && notification.end?.dateTime
                ? `${parseUTCDateString(notification.start.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${parseUTCDateString(notification.end.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : 'N/A'}
            </span>
          </div>
          <div className="h-3 w-px bg-zinc-700/50"></div>
          <div className="flex max-w-full items-center gap-1.5 overflow-hidden">
            <Users className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            <span className="truncate">
              {notification.attendees
                ?.map(
                  (p) =>
                    p.emailAddress?.name ||
                    p.emailAddress?.address ||
                    'Unknown',
                )
                .join(', ') || 'No Attendees'}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area - Only this part scrolls */}
      <div
        ref={scrollContainerRef}
        className="custom-scrollbar relative flex-1 overflow-y-auto bg-zinc-900/20"
      >
        {renderContent()}
        {!isAtBottom && status !== 'loading' && (
          <button
            onClick={scrollToFooter}
            className="animate-bounce-slow sticky bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-full bg-blue-600 p-2 text-white shadow-lg shadow-blue-600/30 transition-all duration-300 hover:scale-105 hover:bg-[#5652CF]"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        )}
        <div ref={footerRef} />
      </div>

      <div className="relative z-10 shrink-0">
        <MeetingSubmissionControls
          notification={notification}
          processedData={processedData}
          transcriptContent={transcriptContent}
          onClose={onClose}
          onStageChat={onStageChat}
        />
      </div>
    </div>
  );
};

const NotificationChip = ({ notification, onClick, onClose }) => {
  const app =
    notification.source_type === 'listener'
      ? notification.listener_data?.app || 'agent'
      : notification.source_type === 'meeting'
        ? 'microsoft_teams'
        : 'agent';
  return (
    <div className="animate-fade-in-fast group relative flex items-center">
      <button
        type="button"
        onClick={onClick}
        className="flex max-w-[220px] items-center gap-2 rounded-full border border-zinc-700/80 bg-zinc-800/90 p-1.5 pr-3 text-xs text-zinc-300 shadow-lg shadow-black/20 backdrop-blur-sm transition-colors hover:bg-zinc-700"
      >
        <img
          // Same missing-src risk as MarkdownRenderer.js's NotificationChip
          // -- an unrecognized listener_data.app would otherwise look up to
          // undefined and React would omit the src attribute entirely.
          src={appImages[app] || appImages.agent}
          alt={app}
          className="h-5 w-5 rounded-full object-contain"
        />
        <span className="truncate text-left font-medium">
          {notification.event_name}
        </span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.(e);
        }}
        className="absolute -right-1.5 -top-1.5 z-10 rounded-full bg-zinc-600 p-0.5 opacity-0 shadow-sm transition-all duration-200 hover:bg-red-500 group-hover:opacity-100"
      >
        <XIcon className="h-3 w-3 text-zinc-200" />
      </button>
    </div>
  );
};

const ListenerNotificationFooter = ({ notification, onClose, onStageChat }) => {
  const { isBusy } = DataState();

  const handleChatStart = () => {
    if (isBusy) return;
    onStageChat(notification, notification.id);
  };

  return (
    <div className="flex w-full items-center justify-end gap-4">
      <span className="mr-auto flex items-center gap-1.5 text-xs text-zinc-500">
        <AlertCircle className="h-3.5 w-3.5" /> Action needed?
      </span>
      <button
        onClick={handleChatStart}
        disabled={isBusy}
        className="flex items-center gap-2 rounded-lg border border-[#5652CF] bg-blue-600 px-5 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-900/20 transition-all hover:-translate-y-0.5 hover:bg-[#5652CF] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MessageSquarePlus className="h-4 w-4" /> Resolve with AI
      </button>
    </div>
  );
};

// ✅ BEAUTIFIED: Main Modal Structure
const InChatNotificationView = ({
  notification,
  onClose,
  inChat = false,
  onStageChat,
}) => {
  const viewRef = useRef();
  const scrollContainerRef = useRef(null);
  const isMeetingNotification = notification?.source_type === 'meeting';
  const [approvalAction, setApprovalAction] = useState({
    isReady: false,
    isLoading: false,
    submit: () => {},
  });
  useOnClickOutside(viewRef, onClose, !approvalAction.isLoading);

  useEffect(() => {
    setApprovalAction({ isReady: false, isLoading: false, submit: () => {} });
  }, [notification]);
  if (!notification) return null;

  const isApproval = notification.source_type === 'workflow';
  let appType = 'agent';
  let title = notification.event_name || notification.subject;
  if (ENABLE_MEETINGS && notification.source_type === 'meeting') {
    appType = 'microsoft_teams';
    title = 'Meeting Summary';
  } else if (notification.source_type === 'listener') {
    appType = notification.listener_data?.app;
    title = notification.event_name;
  } else if (isApproval) {
    appType = 'workflow';
    title = notification.event_name || 'Approval Needed';
  }

  const renderDetail = () => {
    if (isApproval)
      return (
        <ApprovalNotificationDetail
          notification={notification}
          onClose={onClose}
          onReadyStateChange={setApprovalAction}
        />
      );
    if (ENABLE_MEETINGS && notification.source_type === 'meeting')
      return (
        <TeamsMeetingNotificationDetail
          notification={notification}
          scrollContainerRef={scrollContainerRef}
          onClose={onClose}
          onStageChat={onStageChat}
        />
      );
    if (notification.source_type === 'listener') {
      switch (notification.listener_data.app) {
        case 'jira':
          return <JiraNotificationDetail notification={notification} />;
        case 'gmail':
        case 'microsoft_outlook':
          return GmailNotificationDetail ? (
            <GmailNotificationDetail notification={notification} />
          ) : null;
        case 'zoho_crm':
          return <ZohoCrmNotificationDetail notification={notification} />;
        case 'slack':
          return <SlackNotificationDetail notification={notification} />;
        default:
          return (
            <div className="p-8 text-center text-zinc-500">
              No detailed view available.
            </div>
          );
      }
    }
    return <div className="p-8 text-center text-zinc-500">Unknown type.</div>;
  };

  const renderFooter = () => {
    if (isApproval) {
      const nodeType = notification?.workflow_data?.node_type;
      if (nodeType === 'human')
        return (
          <div className="flex shrink-0 items-center justify-end gap-3 rounded-b-xl border-t border-zinc-800 bg-zinc-900 px-6 py-4">
            <button
              onClick={onClose}
              className="rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              Deny
            </button>
            <button
              onClick={onClose}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-emerald-900/20 transition-colors hover:bg-emerald-500"
            >
              Approve
            </button>
          </div>
        );
      if (nodeType === 'action')
        return (
          <div className="flex shrink-0 items-center justify-end rounded-b-xl border-t border-zinc-800 bg-zinc-900 px-6 py-4">
            <button
              onClick={approvalAction.submit}
              disabled={!approvalAction.isReady || approvalAction.isLoading}
              className="flex items-center gap-2 rounded-lg border border-[#5652CF] bg-gradient-to-r from-blue-600 to-[#5652CF] px-6 py-2.5 text-xs font-semibold text-white shadow-lg transition-all hover:shadow-[#5652CF]/25 disabled:cursor-not-allowed disabled:border-zinc-700 disabled:bg-zinc-800 disabled:from-zinc-800 disabled:to-zinc-800 disabled:text-zinc-500"
            >
              {approvalAction.isLoading ? (
                'Processing...'
              ) : (
                <>
                  <ThumbsUp className="h-4 w-4" /> Confirm Decisions
                </>
              )}
            </button>
          </div>
        );
    }
    if (notification.source_type === 'listener')
      return (
        <div className="shrink-0 rounded-b-xl border-t border-zinc-800 bg-zinc-900 px-6 py-4">
          <ListenerNotificationFooter
            notification={notification}
            onClose={onClose}
            onStageChat={onStageChat}
          />
        </div>
      );
    return null;
  };

  return (
    <div className="animate-in fade-in fixed inset-0 z-50 flex items-stretch justify-center bg-black/80 p-0 backdrop-blur-[2px] duration-300 sm:items-center sm:p-4">
      <div
        ref={viewRef}
        className={`animate-in zoom-in-95 relative flex w-full flex-col overflow-hidden border border-zinc-800 bg-[#121214] shadow-2xl ring-1 ring-white/5 duration-300 ${
          isMeetingNotification
            ? 'h-full max-w-none rounded-none sm:h-[92vh] sm:max-w-[min(1400px,calc(100vw-2rem))] sm:rounded-2xl'
            : 'max-h-[85vh] max-w-4xl rounded-none sm:rounded-2xl'
        }`}
      >
        {/* Unified Modal Header - Pinned */}
        <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between border-b border-zinc-800 bg-[#121214] px-5 py-3 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-zinc-700/50 bg-zinc-800/60 p-1.5 shadow-inner">
              <img
                src={appImages[appType] || appImages.agent}
                alt={appType}
                className="h-4 w-4 object-contain"
              />
            </div>
            <div>
              <h3 className="text-sm font-bold leading-none tracking-tight text-zinc-100">
                {title}
              </h3>
              <p className="mt-0.5 flex items-center gap-1 text-[10px] font-medium capitalize text-zinc-500">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500/80"></span>
                {appType?.replace(/_/g, ' ')} Notification
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 transition-all duration-200 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content Body - Flexible & Scrollable */}
        <div
          ref={scrollContainerRef}
          className="relative flex min-h-0 w-full flex-1 flex-col"
        >
          {renderDetail()}
        </div>

        {/* Footer - Pinned */}
        {renderFooter()}
      </div>
    </div>
  );
};

// ===================================================================================
// --- MAIN UI COMPONENT ---
// ===================================================================================

const Ui = () => {
  const {
    currentTaskId,
    activateTask,
    handleCreateTask,
    setJustSavedFiles,
    sendTaskLoading,
    taskInput,
    setTaskInput,
    creatingTask,
    setCreatingTask,
    userGoogleId,
    handleSubmitTask,
    toggleSidebar,
    setToggleSidebar,
    rightSidebarWidth,
    setRightSidebarWidth,
    tasks,
  } = DataState();

  const { taskId: urlTaskId } = useParams();
  const navigate = useNavigate();

  // ── URL → State sync: load the correct chat when navigating via URL ──
  // Handles: page refresh on /chat/:taskId, direct links, browser back/forward.
  // Skips if the task is already active to avoid unnecessary reloads.
  useEffect(() => {
    if (!urlTaskId) {
      // If navigating to /chat (new chat) while an existing chat is loaded, reset conversation state
      if (currentTaskId && !isStagingRef.current) {
        handleCreateTask();
      }
      return;
    }
    if (!tasks || tasks.length === 0) return;
    if (urlTaskId === currentTaskId) return; // already loaded

    const matchedTask = tasks.find((t) => t._id === urlTaskId);
    if (matchedTask) {
      // Shared with Sidebar.js's click handler -- see activateTask in
      // DataProvider.js for why this used to be duplicated here.
      activateTask(matchedTask);
    } else {
      // Task ID in URL doesn't belong to this user — redirect to /chat
      navigate('/chat', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTaskId, tasks]);

  const {
    openNotifications,
    closeNotificationInContext,
    activeNotificationId,
    setActiveNotificationId,
    collapsedNotificationIds,
    setCollapsedNotificationIds,
    createNewTaskForNotification,
  } = useNotifications();

  const {
    activeCRModal,
    closeCRModal,
    openCRModal,
    stagedCRChat,
    clearStagedCRChat,
  } = useChangeRequests();
  const { activeRAModal, closeRAModal } = useResourceAllocation();

  const [pendingContext, setPendingContext] = useState(null);
  const [activeEmailModal, setActiveEmailModal] = useState(null);
  const [activeMeetingModal, setActiveMeetingModal] = useState(null);
  const isStagingRef = useRef(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [tempImages, setTempImages] = useState([]);
  const [justUploadedImages, setJustUploadedImages] = useState(false);
  const isResizing = useRef(false);
  const resizableContainerRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const activeWidthRef = useRef(300);
  const location = useLocation();

  // ── Context-Aware Chat: consume meetingContext from router state ──
  useEffect(() => {
    const meetingCtx = location.state?.meetingContext;
    if (meetingCtx && meetingCtx.llm_summary) {
      if (handleCreateTask) handleCreateTask();
      const title = meetingCtx.meetingTitle || 'Meeting';
      const summary = meetingCtx.llm_summary?.overall_summary || '';
      const excerptLength = Math.min(summary.length, 300);
      const excerpt =
        summary.substring(0, excerptLength) +
        (summary.length > 300 ? '...' : '');

      const jiraIssues = meetingCtx.jira_issues || [];
      const actionItems = meetingCtx.action_items || [];
      const blockers = meetingCtx.blockers || [];

      // Build a rich context payload for the agent
      const contextPayload = {
        event_id: meetingCtx.eventId,
        meeting_id: meetingCtx.eventId,
        event_name: 'meeting_processed',
        source_type: 'meeting',
        app: meetingCtx.isManual ? 'manual_meeting' : 'microsoft_teams',
        subject: title,
        llm_summary: meetingCtx.llm_summary,
        jira_issues: jiraIssues,
        attendees: meetingCtx.attendees || [],
        action_items: actionItems,
        blockers: blockers,
      };

      // Build a context-rich prompt for the agent
      const jiraLine =
        jiraIssues.length > 0
          ? `\n\nJira issues discussed: ${jiraIssues.map((j) => `${j.key} (${j.status}${j.is_blocked ? ', BLOCKED' : ''})`).join(', ')}.`
          : '';
      const actionLine =
        actionItems.length > 0
          ? `\n\nThere are ${actionItems.length} action items, ${blockers.length} blockers to review.`
          : '';

      const promptText = `I'm reviewing the meeting "${title}".${jiraLine}${actionLine}\n\nSummary: ${excerpt}\n\nHelp me with the action items, Jira updates, and blockers from this meeting.`;
      setTaskInput(promptText);

      // Stage the full context for the agent
      setPendingContext(contextPayload);
      if (setCreatingTask) setCreatingTask(true);

      // Clear the router state so it doesn't re-inject on page navigation
      window.history.replaceState({}, document.title);
      if (location.pathname !== '/chat') {
        navigate('/chat', { replace: true });
      }
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the CR modal stages a chat context, absorb it into pendingContext
  // and switch to the new-task view so the user can type their message
  useEffect(() => {
    if (!stagedCRChat) return;
    // Mirrors handleStageChat below: must be set before handleCreateTask()
    // changes currentTaskId/creatingTask, or the auto-close effect further
    // down (keyed on those same values) will fire on the reset and clear
    // notification/chip state right after we stage it.
    isStagingRef.current = true;
    if (handleCreateTask) handleCreateTask();
    setPendingContext(stagedCRChat);
    if (setCreatingTask) setCreatingTask(true);
    clearStagedCRChat();
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
  }, [stagedCRChat]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCollapse = useCallback(() => {
    if (activeNotificationId) {
      setCollapsedNotificationIds((prev) =>
        new Set(prev).add(activeNotificationId),
      );
      setActiveNotificationId(null);
    }
  }, [
    activeNotificationId,
    setActiveNotificationId,
    setCollapsedNotificationIds,
  ]);

  const handleChipClick = (notificationId) => {
    if (activeNotificationId && activeNotificationId !== notificationId) {
      setCollapsedNotificationIds((prev) =>
        new Set(prev).add(activeNotificationId),
      );
    }
    setActiveNotificationId(notificationId);
    setCollapsedNotificationIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
  };

  const handleCloseNotification = (e, notificationId) => {
    e.stopPropagation();
    closeNotificationInContext(notificationId);
  };

  const activeNotification = useMemo(() => {
    if (!openNotifications || !activeNotificationId) return null;
    return openNotifications.find((n) => n.id === activeNotificationId) || null;
  }, [openNotifications, activeNotificationId]);

  // ✅ Auto-close notification logic with staging check
  useEffect(() => {
    if (isStagingRef.current) {
      isStagingRef.current = false;
      return;
    }
    setActiveNotificationId(null);
    setCollapsedNotificationIds(new Set());
  }, [
    currentTaskId,
    creatingTask,
    setActiveNotificationId,
    setCollapsedNotificationIds,
  ]);

  // ✅ NEW: Handle "Chat with Agent" click (Staging Phase)
  const handleStageChat = (payload, notificationId) => {
    if (handleCreateTask) handleCreateTask();
    setPendingContext(payload);
    isStagingRef.current = true;
    if (notificationId) {
      setCollapsedNotificationIds((prev) => new Set(prev).add(notificationId));
    }
    setActiveNotificationId(null); // Close the modal
    if (setCreatingTask) setCreatingTask(true);
    if (location.pathname !== '/chat') {
      navigate('/chat');
    }
  };

  // ✅ NEW: Wrapper for Sending Message
  const handleSmartSend = async (
    messageParam,
    attachments,
    explicitInput = null,
  ) => {
    // Choose the explicitly passed input sequence if provided
    const message = explicitInput !== null ? explicitInput : messageParam;

    if (pendingContext) {
      // Send the pending context along with the user message
      await createNewTaskForNotification(pendingContext, message);
      setPendingContext(null); // Clear pending state
      setCollapsedNotificationIds(new Set()); // Now we can clear the chips
    } else {
      // Standard flow
      await handleSubmitTask(message, explicitInput);
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current || !resizableContainerRef.current) return;
    const containerRect = resizableContainerRef.current.getBoundingClientRect();
    const handleWidth = 8;
    const newWidth = containerRect.right - e.clientX - handleWidth / 2;
    if (newWidth >= 300 && newWidth <= 550) {
      activeWidthRef.current = newWidth;
      if (rightSidebarRef.current) {
        rightSidebarRef.current.style.width = `${newWidth}px`;
      }
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = 'default';
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    setRightSidebarWidth(activeWidthRef.current);
  }, [handleMouseMove, setRightSidebarWidth]);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      isResizing.current = true;
      activeWidthRef.current = rightSidebarWidth;
      document.body.style.cursor = 'col-resize';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // eslint-disable-next-line
    },
    [rightSidebarWidth, handleMouseMove, handleMouseUp],
  );

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounter = useRef(0);
  const processAndUploadFiles = async (fileList) => {
    const filesToProcess = Array.from(fileList);
    if (filesToProcess.length === 0) return;
    if (filesToProcess.length > 10) {
      toast.error('You can upload a maximum of 10 files at a time.');
      return;
    }
    const imagesToUpload = filesToProcess.filter((file) =>
      file.type.startsWith('image/'),
    );
    const otherFilesToUpload = filesToProcess.filter(
      (file) => !file.type.startsWith('image/'),
    );
    if (imagesToUpload.length > 0) {
      const newTempImages = imagesToUpload.map((file) => ({
        id: Date.now() + Math.random(),
        url: URL.createObjectURL(file),
        uploading: true,
      }));
      setTempImages(newTempImages);
    }
    try {
      if (imagesToUpload.length > 0) {
        const imageData = await uploadImages(
          userGoogleId,
          currentTaskId,
          imagesToUpload,
        );
        setTempImages([]);
        if (imageData.error) {
          toast.error('Failed to upload some images.');
        } else {
          setJustUploadedImages(true);
        }
      }
      if (otherFilesToUpload.length > 0) {
        const fileData = await uploadFiles(
          userGoogleId,
          currentTaskId,
          otherFilesToUpload,
        );
        if (fileData && !fileData.error) {
          setJustSavedFiles(true);
        } else {
          toast.error('Failed to upload some files.');
        }
      }
      toast.success('Files uploaded successfully!');
    } catch (error) {
      toast.error('An error occurred while uploading files.');
    } finally {
      setTempImages([]);
    }
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    dragCounter.current = 0;
    if (!sendTaskLoading) {
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processAndUploadFiles(e.dataTransfer.files);
      }
    }
  };
  const handleMobileSidebarToggle = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };
  const handleDesktopToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setToggleSidebar(!toggleSidebar);
  };
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileSidebarOpen]);

  const [showFeedback, setShowFeedback] = useState(false);

  const handleFeedbackSubmit = (data) => {
    setShowFeedback(false);
  };

  const [morningBriefing, setMorningBriefing] = useState(false);

  return (
    <div className="bg-3 text-p-text relative flex h-screen max-h-[100svh] w-screen flex-col gap-2 overflow-hidden p-2 md:flex-row md:gap-2 md:p-2">
      <div
        className={`${toggleSidebar ? 'hidden md:block md:w-16' : 'hidden md:block md:w-56'} relative h-full flex-shrink-0 transform overflow-hidden rounded-xl border border-zinc-700 transition-all duration-150 md:rounded-2xl`}
      >
        {/* MODIFIED: REMOVED button from here so it doesn't overlap */}
        <Sidebar
          toggleSidebar={toggleSidebar}
          isOpen={false}
          onToggle={() => {}}
        />
      </div>
      <div className="md:hidden">
        <Sidebar
          toggleSidebar={false}
          isOpen={mobileSidebarOpen}
          onToggle={handleMobileSidebarToggle}
        />
      </div>
      <div ref={resizableContainerRef} className="flex h-full min-w-0 flex-1">
        <div
          className={`bg-1 relative min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-xl border border-zinc-700 pb-3 pt-2 md:gap-7 md:rounded-2xl md:pb-4 md:pt-4  ${morningBriefing ? 'hidden' : 'flex'}`}
        >
          <div
            className="absolute left-4 z-50 flex flex-col items-start gap-2 transition-all duration-300 ease-in-out"
            style={{ top: toggleSidebar ? '2.5rem' : '1rem' }}
          >
            {(openNotifications || [])
              .filter((n) => collapsedNotificationIds?.has(n.id))
              .map((n) => (
                <NotificationChip
                  key={n.id}
                  notification={n}
                  onClick={() => handleChipClick(n.id)}
                  onClose={(e) => handleCloseNotification(e, n.id)}
                />
              ))}

            {/* Standalone chip for email / meeting / CR "Chat with Agent" —
                opens the source modal on click. "meeting" was missing here:
                MeetingDetailModal.jsx's "Bring to chat" already staged this
                same pendingContext via stageCRChat, same as EmailModal's
                does for mail -- the chip and its reopen handler just never
                learned about that source_type, so the chip silently never
                appeared and there was no way back to the meeting from
                chat. */}
            {(pendingContext?._synthetic ||
              pendingContext?.source_type === 'change_request' ||
              pendingContext?.source_type === 'deliverable' ||
              pendingContext?.source_type === 'mail' ||
              (ENABLE_MEETINGS &&
                pendingContext?.source_type === 'meeting')) && (
              <NotificationChip
                notification={pendingContext}
                onClick={() => {
                  // Sidebar tickets/todos/meetings: nothing to reopen, and
                  // the email fallback below would misread their id.
                  if (pendingContext?._noReopen) return;
                  if (pendingContext?.source_type === 'change_request') {
                    if (pendingContext?.cr_data) {
                      openCRModal(pendingContext.cr_data);
                    }
                  } else if (pendingContext?.source_type === 'meeting') {
                    const eventId =
                      pendingContext?.event_id || pendingContext?.meeting_id;
                    if (eventId) {
                      setActiveMeetingModal({
                        eventId,
                        isManual: pendingContext?.app === 'manual_meeting',
                      });
                    }
                  } else {
                    const msgId =
                      pendingContext?.message_id ||
                      pendingContext?.emailData?.message_id ||
                      pendingContext?.emailData?.id ||
                      pendingContext?.id;
                    const isOutlook =
                      pendingContext?.isOutlook ??
                      pendingContext?.listener_data?.app ===
                        'microsoft_outlook';
                    if (msgId) {
                      setActiveEmailModal({ messageId: msgId, isOutlook });
                    }
                  }
                }}
                onClose={(e) => {
                  e.stopPropagation();
                  setPendingContext(null);
                }}
              />
            )}
          </div>

          <InChatNotificationView
            notification={activeNotification}
            onClose={handleCollapse}
            onStageChat={handleStageChat}
          />
          <div className="z-30">
            <Tabbar absolute={true} />
          </div>
          {(integrationData.length > 0 || ENABLE_MCP) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowIntegrations(true);
              }}
              className="integration-button absolute right-2 top-2 z-20 flex items-center gap-1 rounded-full border border-zinc-500 px-2 py-1 text-xs transition-colors hover:bg-white hover:bg-opacity-10 md:right-4 md:gap-2 md:px-2.5 md:py-1.5"
            >
              <FiLink className="text-sm text-[#5B58DB]" />
              <span className="hidden sm:inline">Integrations</span>
            </button>
          )}
          <button
            className="absolute left-2 top-2 z-10 hidden rounded-lg p-1 text-lg transition-colors hover:bg-white hover:bg-opacity-10 md:block md:text-xl"
            onClick={handleDesktopToggle}
          >
            <BiSidebar />
          </button>
          <div
            className="mt-12 flex max-h-full min-h-0 flex-1 flex-col md:mt-0"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {creatingTask ? (
              <div className="mx-auto -mt-10 mb-3 flex min-h-screen w-full max-w-4xl flex-1 flex-col items-center justify-center px-4">
                {/* Heading Section */}
                <div className="flex flex-col items-center justify-center gap-6 px-2 text-center md:gap-10">
                  <div className="space-y-2 md:space-y-4">
                    <h1 className="flex items-end justify-center gap-2 text-2xl font-bold leading-tight md:text-3xl md:leading-snug lg:text-4xl">
                      <div>
                        <span className="text-3xl font-medium">
                          SuperManager AGI
                        </span>
                        <span className="gradient-text ml-2 text-3xl font-normal">
                          enterprise
                        </span>
                      </div>
                    </h1>
                    <span className="mt-2 text-lg font-light text-zinc-200">
                      ⚡Less Talking, More Delivery
                    </span>
                  </div>
                </div>

                {/* Textarea Section - Centered */}
                <div className="mt-8 w-full max-w-3xl md:mt-12">
                  <AutoResizableTextarea
                    setInput={setTaskInput}
                    input={taskInput}
                    handleSend={(messageToSend) => {
                      if (messageToSend) {
                        setTaskInput(messageToSend);
                      }
                      handleSmartSend(null, null, messageToSend);
                    }}
                    isDraggingOver={isDraggingOver}
                    tempImages={tempImages}
                    justUploadedImages={justUploadedImages}
                    setJustUploadedImages={setJustUploadedImages}
                    processAndUploadFiles={processAndUploadFiles}
                    setTempImages={setTempImages}
                    newChat={creatingTask}
                  />
                </div>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-hidden">
                <ChatList
                  tempImages={tempImages}
                  isDraggingOver={isDraggingOver}
                  setTempImages={setTempImages}
                  justUploadedImages={justUploadedImages}
                  setJustUploadedImages={setJustUploadedImages}
                  processAndUploadFiles={processAndUploadFiles}
                />
              </div>
            )}
          </div>
        </div>
        {/* eslint-disable-next-line */}
        <div
          className={`group hidden w-2 flex-shrink-0 cursor-col-resize items-center justify-center ${morningBriefing ? 'hidden' : 'md:flex'}`}
          onMouseDown={handleMouseDown}
        >
          <div className="h-12 w-0.5 rounded-full bg-zinc-700 transition-colors duration-200 group-hover:bg-[#5652CF]" />
        </div>
        {/* <div className="hidden h-full overflow-hidden md:block" style={{ width: `${rightSidebarWidth}px` }}>
          <RightSideActionTracker />
        </div> */}
        <div
          ref={rightSidebarRef}
          className={
            morningBriefing
              ? 'h-full flex-1 overflow-hidden'
              : 'hidden h-full overflow-hidden md:block'
          }
          style={morningBriefing ? {} : { width: `${rightSidebarWidth}px` }}
        >
          <RightSideActionTracker
            isMorningBriefing={morningBriefing}
            onMorningBriefing={setMorningBriefing}
            onStageChat={handleStageChat}
          />
        </div>
      </div>
      {showIntegrations && (
        <div className="fixed inset-0 z-50">
          <Integration
            onClose={() => setShowIntegrations(false)}
            user_google_id={userGoogleId}
          />
        </div>
      )}
      <IntegrationTutorial
        onIntegrationClick={() => setShowIntegrations(true)}
      ></IntegrationTutorial>

      {ENABLE_FEEDBACK && (
        <button
          onClick={() => setShowFeedback(true)}
          className="fixed right-6 z-40 hidden rounded-full bg-[#5652CF] p-4 text-white shadow-lg transition-all hover:bg-[#5652CF]/90 md:block"
          style={{
            bottom: morningBriefing ? '96px' : '72px',
          }}
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </button>
      )}

      {/* Feedback Modal */}
      {ENABLE_FEEDBACK && showFeedback && (
        <FeedbackComponent
          onClose={() => setShowFeedback(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}

      {/* Change Request Detail Modal */}
      {activeCRModal && (
        <CRDetailModal cr={activeCRModal} onClose={closeCRModal} />
      )}

      {/* Resource Allocation Modal */}
      {activeRAModal && (
        <ResourceAllocationModal data={activeRAModal} onClose={closeRAModal} />
      )}

      {/* Email Detail Modal Dialog */}
      {activeEmailModal && (
        <EmailComponent
          messageId={activeEmailModal.messageId}
          isOutlook={activeEmailModal.isOutlook}
          onClose={() => setActiveEmailModal(null)}
        />
      )}

      {/* Meeting Detail Modal Dialog -- reopened from the "Bring to chat"
          chip, mirroring the Email Detail Modal above. */}
      {activeMeetingModal && (
        <MeetingDetailModal
          eventId={activeMeetingModal.eventId}
          isManual={activeMeetingModal.isManual}
          onClose={() => setActiveMeetingModal(null)}
        />
      )}
    </div>
  );
};

export default Ui;
